import * as signalR from '@microsoft/signalr';

export interface PrintJobStatusEvent {
  printJobLogId: number;
  printJobId: string;
  status: string;
  statusCode: number;
  isTerminal: boolean;
  totalDurationSeconds: number | null;
  timeToSubmitSeconds: number | null;
  ezeepProcessingSeconds: number | null;
}

export type PrintJobStatusCallback = (event: PrintJobStatusEvent) => void;

class PrintJobSignalRService {
  private connection: signalR.HubConnection | null = null;
  private callbacks: Set<PrintJobStatusCallback> = new Set();
  private isConnecting = false;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;

  async connect(): Promise<void> {
    if (this.connection?.state === signalR.HubConnectionState.Connected) {
      return;
    }

    if (this.isConnecting) {
      return;
    }

    this.isConnecting = true;

    try {
      this.connection = new signalR.HubConnectionBuilder()
        .withUrl('/updateHub')
        .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
        .configureLogging(signalR.LogLevel.Warning)
        .build();

      this.connection.onreconnecting((error) => {
        console.log('[PrintJobSignalR] Reconnecting...', error?.message);
      });

      this.connection.onreconnected((connectionId) => {
        console.log('[PrintJobSignalR] Reconnected:', connectionId);
        this.reconnectAttempts = 0;
      });

      this.connection.onclose((error) => {
        console.log('[PrintJobSignalR] Connection closed:', error?.message);
        this.handleConnectionClosed();
      });

      this.connection.on('PrintJobStatusUpdated', (event: PrintJobStatusEvent) => {
        this.notifyCallbacks(event);
      });

      await this.connection.start();
      console.log('[PrintJobSignalR] Connected');
      this.reconnectAttempts = 0;
    } catch (error) {
      console.error('[PrintJobSignalR] Connection failed:', error);
      this.handleConnectionClosed();
    } finally {
      this.isConnecting = false;
    }
  }

  private handleConnectionClosed(): void {
    if (this.reconnectAttempts < this.maxReconnectAttempts && this.callbacks.size > 0) {
      this.reconnectAttempts++;
      const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000);
      console.log(`[PrintJobSignalR] Reconnect in ${delay}ms (attempt ${this.reconnectAttempts})`);
      setTimeout(() => this.connect(), delay);
    }
  }

  subscribe(callback: PrintJobStatusCallback): () => void {
    this.callbacks.add(callback);

    if (this.connection?.state !== signalR.HubConnectionState.Connected && !this.isConnecting) {
      this.connect();
    }

    return () => {
      this.callbacks.delete(callback);
      if (this.callbacks.size === 0) {
        this.disconnect();
      }
    };
  }

  private notifyCallbacks(event: PrintJobStatusEvent): void {
    this.callbacks.forEach((callback) => {
      try {
        callback(event);
      } catch (error) {
        console.error('[PrintJobSignalR] Callback error:', error);
      }
    });
  }

  async disconnect(): Promise<void> {
    if (this.connection) {
      try {
        await this.connection.stop();
        console.log('[PrintJobSignalR] Disconnected');
      } catch (error) {
        console.error('[PrintJobSignalR] Disconnect error:', error);
      }
      this.connection = null;
    }
  }

  isConnected(): boolean {
    return this.connection?.state === signalR.HubConnectionState.Connected;
  }
}

let serviceInstance: PrintJobSignalRService | null = null;

export function getPrintJobSignalR(): PrintJobSignalRService {
  if (!serviceInstance) {
    serviceInstance = new PrintJobSignalRService();
  }
  return serviceInstance;
}

export default PrintJobSignalRService;
